import { left, right } from 'fp-ts/lib/Either';
import { compose as lcompose } from 'lodash/fp';
import { assocPath, pathOr, pick } from 'rambda';
import { csosUpmApi, IAddress, IUser, ResponseCSOSHeaders } from '..';

export interface UpdateUserProfile {
  address: {
    city: string;
    postalCd: string;
    stateCd: string;
    street1: string;
    street2: string;
    street3: string;
  };
  certificateEmail: string;
  person: {
    middleInitial: string;
  };
  phone: string;
}

export const updateUser =
  (hR: ResponseCSOSHeaders) => async (update: UpdateUserProfile) => {
    if (`${import.meta.env.VITE_DEPLOYED_IN}` !== '') {
      return await csosUpmApi(hR)
        .put(`/v1/api/upm/user-profile`, update)
        .then(r => right(r.data))
        .catch(e => left(e));
    } else {
      return Promise.resolve(right(update));
    }
  };

export function getUpdateUserProfile(
  user: IUser,
  phone: string,
  address: IAddress
): UpdateUserProfile {
  const { icam } = user;
  if (!icam) {
    throw new Error('user.icam is undefined');
  }

  return lcompose(
    assocPath('person.middleInitial', icam.data.profile.person.middleInitial),
    lcompose(
      assocPath('address'),
      pick(['city', 'postalCd', 'stateCd', 'street1', 'street2', 'street3'])
    )(address)
  )({
    phone,
    certificateEmail: icam.data.profile.certificateEmail,
  });
}

export const searchEmailsMinLength = 3;

export const searchEmails =
  (hR: ResponseCSOSHeaders) =>
  (email: string): Promise<string[]> =>
    email.length < searchEmailsMinLength
      ? new Promise(resolve => {
          resolve([]);
        })
      : csosUpmApi(hR)
          .post(`v1/api/team/emails`, { email })
          .then(lcompose(pathOr([], 'data')));

export const searchSerialNumbers =
  (hR: ResponseCSOSHeaders) =>
  (serialNumber: string): Promise<string[]> =>
    serialNumber.length < searchEmailsMinLength
      ? new Promise(resolve => {
          resolve([]);
        })
      : csosUpmApi(hR)
          .post(`v1/api/team/serial-numbers`, { serialNumber })
          .then(lcompose(pathOr([], 'data')));

export const searchUserByFirstNameMinLength = 2;

export const searchUserByFirstName =
  (hR: ResponseCSOSHeaders) =>
  (firstName: string): Promise<string[]> =>
    firstName.length < searchUserByFirstNameMinLength
      ? new Promise(resolve => {
          resolve([]);
        })
      : csosUpmApi(hR)
          .post(`v1/api/team/first-name`, { firstName })
          .then(lcompose(pathOr([], 'data')));

export const searchUserByLastNameMinLength = 2;

export const searchUserByLastName =
  (hR: ResponseCSOSHeaders) =>
  (lastName: string): Promise<string[]> =>
    lastName.length < searchUserByLastNameMinLength
      ? new Promise(resolve => {
          resolve([]);
        })
      : csosUpmApi(hR)
          .post(`v1/api/team/last-name`, { lastName })
          .then(lcompose(pathOr([], 'data')));
